import React, { useEffect, useMemo, useState } from "react";
import FieldBlock from "../FieldBlock";
import { connect, useDispatch } from "react-redux";
import {
  handleClaimItemFileUpload,
  removeClaimItemDamage,
  removeClaimItemImage,
} from "../../../store/ace2-claim/actions";
import DamageBox from "./DamageBox";
import { useFieldArray, useFormContext } from "react-hook-form";
import { Damage } from "../../../types/damage";
import { RootState } from "../../../store/root-reducer";
import moment from "moment";
import { useTranslation } from "react-i18next";
import FileUploader from "./FileUploader";
import { IoEyeOutline } from "react-icons/io5";
import { GoTrash } from "react-icons/go";
import { Heic, Pdf } from "../../../assets/svg";
import { getPreSignedUrl } from "../../../helper";

function ClaimPhotoThumbnail({
  fileUrl,
  fileName,
}: {
  fileUrl: string;
  fileName: string;
}) {
  const [hasError, setHasError] = useState(false);
  const lowerName = fileName?.toLowerCase() || "";

  if (lowerName.includes("pdf")) {
    return <Pdf />;
  }
  if (lowerName.includes("heic") || hasError) {
    return <Heic />;
  }
  return (
    <img
      src={fileUrl}
      onError={() => setHasError(true)}
      className="h-12 w-12 rounded-full aspect-square object-cover shrink-0 overflow-hidden"
      alt=""
    />
  );
}

function ClaimItemBox({ idx, item, claimId, plan, claimOptions }) {
  const documents_and_photos = item?.documents_and_photos || [];
  const claimItemId = item?.id;
  const material = item?.material;
  const { control, getValues, clearErrors } = useFormContext();
  const { fields, append, remove, update } = useFieldArray({
    control,
    name: `claimItemDamage.[${claimItemId}]`,
  });
  const disptach = useDispatch();
  const { t } = useTranslation();

  const {
    damageTypesOptions = [],
    specificDamageOptions = {},
    specificLocationOptions = [],
    occuredReasonOptions = [],
    actionTakenOptions = [],
  } = useMemo(() => {
    const {
      howDidOccur = [],
      actionsTaken = [],
      specificLocations = [],
      specificDamageTypesCombined = [],
    } = claimOptions;
    const specificLocationOptions =
      specificLocations?.map((v) => ({
        label: v.name,
        value: v.name.toString(),
      })) || [];
    const occuredReasonOptions =
      howDidOccur?.map((v) => ({
        label: v.name,
        value: v.name?.toString(),
      })) || [];
    const actionTakenOptions =
      actionsTaken?.map((v) => ({
        label: v.name,
        value: v.name?.toString(),
      })) || [];

    const specificDamageOptions = {};
    const damageTypesOptions = [];

    specificDamageTypesCombined.forEach(
      ({ material: itemMaterial, damage, specific_damage }) => {
        if (itemMaterial === material) {
          if (!specificDamageOptions[damage]) {
            damageTypesOptions.push({
              label: damage.toString(),
              value: damage.toString(),
            });
            specificDamageOptions[damage] = [];
          }
          specificDamageOptions[damage].push({
            label: specific_damage.toString(),
            value: specific_damage.toString(),
          });
        }
      }
    );
    return {
      specificLocationOptions,
      occuredReasonOptions,
      actionTakenOptions,
      specificDamageOptions,
      damageTypesOptions,
    };
  }, [claimOptions, material]);

  useEffect(() => {
    if (documents_and_photos.length > 0) {
      clearErrors(`claimItemDamageImage.[${claimItemId}].invoice`);
    }
    if (documents_and_photos.length > 1) {
      clearErrors(`claimItemDamageImage.[${claimItemId}].files`);
    }
    return () => {};
  }, [claimItemId, clearErrors, documents_and_photos.length]);

  useEffect(() => {
    let index = 0;
    const updatedFormValues =
      getValues(`claimItemDamage.[${claimItemId}]`) || [];
    do {
      const damageData: Damage = item?.damages?.[index] || {};
      const updatedFormValues =
        getValues(`claimItemDamage.[${claimItemId}]`) || [];
      // const existFieldsIndex = updatedFormValues.findIndex(
      //   (field) =>
      //     field?.["index"] === index || field?.["damageId"] === damageData?.id
      // );
      const safeIndex = index;
      const existFieldsIndex = updatedFormValues.findIndex(
        (field) => field?.["index"] === safeIndex
      );
      if (existFieldsIndex + 1) {
        update(existFieldsIndex, {
          ...(updatedFormValues?.[existFieldsIndex] || {}),
          ...(damageData?.id ? { damageId: damageData?.id } : {}),
          isSavedInCRM: Boolean(damageData?.id),
        });
      } else {
        append({
          ...(damageData?.id ? { damageId: damageData?.id } : {}),
          index,
          isSavedInCRM: Boolean(damageData?.id),
          damage_category: damageData?.damageCategory || "",
          specific_damage: damageData?.specificDamage || "",
          damage_area: damageData?.specificLocation || "",
          when_did_you_notice_the_damage: damageData?.damageDate
            ? moment(damageData?.damageDate).format("YYYY-MM-DD")
            : null,
          how_did_it_happen: damageData?.occuredReason || "",
          did_you_take_any_steps_to_remedy: damageData?.actionTaken || "",
          note: damageData?.notes || "",
        });
      }
      index++;
    } while (index < item?.damages?.length);

    return () => {};
  }, [append, claimItemId, getValues, item?.damages, update]);

  const handleDeleteClick = (idx, item) => {
    if (item.isSavedInCRM) {
      disptach(removeClaimItemDamage(item?.damageId));
    }
    remove(idx);
  };

  return (
    <div>
      <div className="bg-[#EBEEEF] min-h-8 rounded flex items-center px-2 gap-2 overflow-hidden">
        <span className="size-5 flex justify-center items-center  bg-white rounded-full p-3">
          {idx + 1}
        </span>
        <div className="text-black font-semibold  block">
          {item?.warrantyItem?.name}
        </div>
      </div>
      <div className="bg-white grid gap-1.5 divide-y-[1px] divide-[#F0F0F0] px-2 py-2">
        <FieldBlock label="Purchase Date" value={plan.purchaseDate} date />
        <FieldBlock label="Store Code" value={plan?.store} />
        <FieldBlock label="Delivery Date" value={plan.deliveryDate} date />
        <FieldBlock
          label="Total Purchase Price"
          value={item?.warrantyItem?.itemPrice}
          price
        />
      </div>
      <FileUploader
        control={control}
        name={`claimItemDamageImage.[${claimItemId}].invoice`}
        shouldUnregister
        onFileSelect={(files, callBack) => {
          disptach(
            handleClaimItemFileUpload({
              files,
              claimItemId: item.id,
              callBack,
            })
          );
        }}
        label="Dealer invoice or receipt"
        placeholder="Upload invoice or receipt file..."
      />
      <FileUploader
        control={control}
        name={`claimItemDamageImage.[${claimItemId}].files`}
        shouldUnregister
        isMultiple
        onFileSelect={(files, callBack) => {
          disptach(
            handleClaimItemFileUpload({
              files,
              claimItemId: item.id,
              callBack,
            })
          );
        }}
        rules={{
          validate: () => {
            return documents_and_photos?.length > 1
              ? true
              : "Please upload required documents";
          },
        }}
        placeholder="Upload damage images.."
        label="Pictures of the damage to the product including pictures of any serial numbers or labels."
      />
      {documents_and_photos.length > 0 && (
        <>
          <div className="bg-[#F7F7F7] my-2 text-black font-semibold  min-h-8 rounded flex items-center px-2 gap-2 overflow-hidden">
            {t("Uploaded Files")}
          </div>
          <div className="grid gap-y3 divide-y">
            {documents_and_photos.map((fileName) => {
              const fileUrl = getPreSignedUrl({
                claimId,
                fileName: fileName?.replace(/\.heic$/i, ".png"),
              });
              return (
                <div
                  className="flex flex-row  items-center justify-between py-1.5"
                  key={`${claimId}-${fileName}`}
                >
                  <div className="flex  flex-row gap-2 items-center">
                    <ClaimPhotoThumbnail
                      fileUrl={fileUrl}
                      fileName={fileName}
                    />
                    <p className="text-sm">{fileName}</p>
                  </div>
                  <div className="flex shrink-0 flex-row gap-2 items-center">
                    <button type="button" onClick={() => window.open(fileUrl)}>
                      <IoEyeOutline color="#2D6CCA" size={18} />
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        disptach(
                          removeClaimItemImage({
                            claimItemId: item.id,
                            fileName,
                          })
                        )
                      }
                    >
                      <GoTrash color="#DB3C32" size={18} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
      <div className="space-y-2">
        <div className="space-y-3">
          {fields.map((damage, idx) => (
            <DamageBox
              key={damage?.id}
              idx={idx}
              item={damage}
              claimItemId={claimItemId}
              onDelete={handleDeleteClick}
              specificDamageOptions={specificDamageOptions}
              specificLocationOptions={specificLocationOptions}
              occuredReasonOptions={occuredReasonOptions}
              actionTakenOptions={actionTakenOptions}
              damageTypesOptions={damageTypesOptions}
            />
          ))}
          <button
            type="button"
            className="border-[#2D6CCA] p-2 text-[#2D6CCA] hover:bg-[#2D6CCA] hover:text-white text-sm font-medium  border rounded-md"
            onClick={() => {
              append({
                index: item.damages.length,
                damage_category: "",
                specific_damage: "",
                damage_area: "",
                how_did_it_happen: "",
                when_did_you_notice_the_damage: null,
                did_you_take_any_steps_to_remedy: "",
                note: "",
                isSavedInCRM: false,
              });
            }}
          >
            {t("+ Add Damage")}
          </button>
        </div>
      </div>
    </div>
  );
}

const mapStateToProps = (state: RootState) => ({
  claimId: state?.ace2Claim.claimId || "",
  plan: state?.ace2Claim.plan || "",
  claimOptions: state?.user?.claimOptions || {},
});

export default connect(mapStateToProps)(ClaimItemBox);
